import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarDays, QrCode } from "lucide-react";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import useAdminApi from "@/hooks/useAdminApi";
import useAdminMutation from "@/hooks/useAdminMutation";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";
import { TICKET_STATUS_LABEL_KEY, labelKeyFor } from "@/i18n/racketLabMaps";

/**
 * RLAdminCalendar — SS-4 (Jun 2026).
 *
 * Admin-only / preview-first month agenda fed by the read-only
 * `GET /admin/racket-lab/calendar` endpoint:
 *   • month grid (Mon-first) with ticket chips + shift badges per day,
 *   • prev / next / today navigation,
 *   • shop + operator filters,
 *   • day panel listing tickets (→ detail) and shifts of the clicked day,
 *   • "unscheduled" programmable queue: active tickets without
 *     `scheduled_for`, schedulable inline via the EXISTING
 *     `PATCH /admin/racket-lab/tickets/{id}` (no new write endpoints).
 *
 * HARD BLOCKS preserved: no customer-facing, no legal runtime, no
 * Q28/emails, no public scan, no production.
 */
const STATUS_DOT = {
  draft: "bg-slate-400",
  checked_in: "bg-blue-500",
  assigned: "bg-indigo-500",
  in_progress: "bg-amber-500",
  pending_customer_approval: "bg-yellow-500",
  ready_for_pickup: "bg-emerald-500",
  closed: "bg-slate-300",
  cancelled: "bg-rose-400",
  deleted: "bg-slate-300",
};

const pad = (n) => String(n).padStart(2, "0");
const todayIso = () => new Date().toISOString().slice(0, 10);

const RLAdminCalendar = () => {
  const t = useRacketLabAdminT();
  const navigate = useNavigate();
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [shopFilter, setShopFilter] = useState("");
  const [operatorFilter, setOperatorFilter] = useState("");
  const [selectedDay, setSelectedDay] = useState(todayIso());
  const [scheduleDates, setScheduleDates] = useState({});
  const { run, loading: mutating } = useAdminMutation();

  const daysInMonth = new Date(ym.y, ym.m + 1, 0).getDate();
  const dateFrom = `${ym.y}-${pad(ym.m + 1)}-01`;
  const dateTo = `${ym.y}-${pad(ym.m + 1)}-${pad(daysInMonth)}`;
  const leadingBlanks = (new Date(ym.y, ym.m, 1).getDay() + 6) % 7; // Monday-first

  const params = useMemo(() => {
    const p = { date_from: dateFrom, date_to: dateTo };
    if (shopFilter) p.racket_lab_shop_id = shopFilter;
    if (operatorFilter) p.racket_lab_operator_id = operatorFilter;
    return p;
  }, [dateFrom, dateTo, shopFilter, operatorFilter]);

  const { data, loading, error, reload } = useAdminApi("/admin/racket-lab/calendar", { params });
  const { data: shopsData } = useAdminApi("/admin/racket-lab/shops");
  const { data: opsData } = useAdminApi("/admin/racket-lab/operators");

  const shops = shopsData?.items || (Array.isArray(shopsData) ? shopsData : []);
  const operators = opsData?.items || (Array.isArray(opsData) ? opsData : []);
  const days = data?.days || {};
  const unscheduled = data?.unscheduled_active || [];
  const selected = days[selectedDay] || { tickets: [], shifts: [] };

  const monthLabel = new Date(ym.y, ym.m, 1).toLocaleDateString(undefined, {
    month: "long", year: "numeric",
  });

  const shiftMonth = (delta) =>
    setYm(({ y, m }) => {
      const d = new Date(y, m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });

  const goToday = () => {
    const d = new Date();
    setYm({ y: d.getFullYear(), m: d.getMonth() });
    setSelectedDay(todayIso());
  };

  const scheduleTicket = async (id) => {
    const date = scheduleDates[id];
    if (!date || mutating) return;
    const res = await run("patch", `/admin/racket-lab/tickets/${id}`, { scheduled_for: date }).catch(() => null);
    if (res) {
      setScheduleDates((s) => ({ ...s, [id]: "" }));
      reload();
    }
  };

  const weekdayKeys = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

  return (
    <div data-testid="rl-admin-calendar-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />

      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{t("calendar.title")}</h1>
          <p className="text-xs text-slate-500">{t("calendar.subtitle")}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button type="button" data-testid="rl-calendar-prev" onClick={() => shiftMonth(-1)}
            className="rounded-md border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50">
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <span data-testid="rl-calendar-month-label" className="min-w-[140px] text-center text-sm font-medium capitalize text-slate-800">
            {monthLabel}
          </span>
          <button type="button" data-testid="rl-calendar-next" onClick={() => shiftMonth(1)}
            className="rounded-md border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50">
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
          <button type="button" data-testid="rl-calendar-today" onClick={goToday}
            className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50">
            {t("calendar.today")}
          </button>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <select
          data-testid="rl-calendar-filter-shop"
          value={shopFilter}
          onChange={(e) => setShopFilter(e.target.value)}
          className="rounded-md border border-slate-300 px-2 py-1.5 text-xs"
        >
          <option value="">{t("calendar.filter_all_shops")}</option>
          {shops.map((s) => (
            <option key={s.id} value={s.id}>{s.name || s.id}</option>
          ))}
        </select>
        <select
          data-testid="rl-calendar-filter-operator"
          value={operatorFilter}
          onChange={(e) => setOperatorFilter(e.target.value)}
          className="rounded-md border border-slate-300 px-2 py-1.5 text-xs"
        >
          <option value="">{t("calendar.filter_all_operators")}</option>
          {operators.map((o) => (
            <option key={o.id} value={o.id}>{o.label || o.display_label || o.id}</option>
          ))}
        </select>
        {!loading && data?.totals ? (
          <span data-testid="rl-calendar-totals" className="text-[10px] text-slate-500">
            {t("calendar.totals_line", {
              tickets: data.totals.scheduled_tickets,
              shifts: data.totals.shifts,
              unscheduled: data.totals.unscheduled_active,
            })}
          </span>
        ) : null}
      </div>

      {loading && (
        <div data-testid="rl-calendar-loading" className="rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-500">
          {t("calendar.loading")}
        </div>
      )}
      {!loading && error && (
        <div data-testid="rl-calendar-error" className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          {t("dashboard.error_prefix")} {error.status || ""}: {error.detail || t("dashboard.error_unknown")}
        </div>
      )}

      {!loading && !error && (
        <>
          <div data-testid="rl-calendar-grid" className="grid grid-cols-7 gap-1">
            {weekdayKeys.map((k) => (
              <div key={k} className="px-1 py-0.5 text-center text-[10px] font-semibold uppercase text-slate-500">
                {t(`calendar.weekdays.${k}`)}
              </div>
            ))}
            {Array.from({ length: leadingBlanks }).map((_, i) => (
              <div key={`blank-${i}`} className="min-h-[72px] rounded-md bg-slate-50/50" />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const date = `${ym.y}-${pad(ym.m + 1)}-${pad(i + 1)}`;
              const cell = days[date];
              const isToday = date === todayIso();
              const isSelected = date === selectedDay;
              return (
                <button
                  type="button"
                  key={date}
                  data-testid={`rl-calendar-day-${date}`}
                  onClick={() => setSelectedDay(date)}
                  className={`flex min-h-[72px] flex-col items-stretch gap-0.5 rounded-md border p-1 text-left transition-colors ${
                    isSelected ? "border-emerald-500 bg-emerald-50/40" : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                >
                  <span className={`self-end rounded-full px-1.5 text-[10px] font-medium ${
                    isToday ? "bg-emerald-600 text-white" : "text-slate-500"
                  }`}>
                    {i + 1}
                  </span>
                  {(cell?.tickets || []).slice(0, 3).map((tk) => (
                    <span key={tk.id} className="flex items-center gap-1 truncate rounded bg-slate-100 px-1 text-[9px] text-slate-700">
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${STATUS_DOT[tk.status] || "bg-slate-400"}`} />
                      {tk.equipment_reference_label || tk.id.slice(0, 8)}
                    </span>
                  ))}
                  {(cell?.tickets || []).length > 3 ? (
                    <span className="text-[9px] text-slate-500">+{cell.tickets.length - 3}</span>
                  ) : null}
                  {(cell?.shifts || []).length ? (
                    <span className="mt-auto self-start rounded-full bg-sky-100 px-1.5 text-[9px] text-sky-700">
                      {t("calendar.shift_chip", { count: cell.shifts.length })}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Day panel */}
            <section data-testid="rl-calendar-day-panel" className="rounded-md border border-slate-200 bg-white p-4">
              <h2 className="mb-2 text-sm font-semibold text-slate-700">
                {t("calendar.day_panel_title", { date: selectedDay })}
              </h2>
              {selected.tickets.length === 0 && selected.shifts.length === 0 ? (
                <p data-testid="rl-calendar-day-empty" className="text-xs text-slate-500">{t("calendar.day_panel_empty")}</p>
              ) : (
                <>
                  {selected.tickets.length ? (
                    <>
                      <p className="text-[10px] uppercase tracking-wide text-slate-500">{t("calendar.tickets_label")}</p>
                      <ul className="mb-2 mt-1 space-y-1 text-xs">
                        {selected.tickets.map((tk) => (
                          <li key={tk.id} className="flex items-center gap-1">
                            <button
                              type="button"
                              data-testid={`rl-calendar-ticket-${tk.id}`}
                              onClick={() => navigate(`/admin/racket-lab/tickets/${tk.id}`)}
                              className="flex flex-1 items-center justify-between gap-2 rounded px-1 py-0.5 text-left hover:bg-slate-50"
                            >
                              <span className="truncate text-slate-700">{tk.equipment_reference_label || tk.id.slice(0, 8)}</span>
                              <span className="flex shrink-0 items-center gap-1.5">
                                {tk.racket_lab_operator_id ? (
                                  <code className="text-[9px] text-slate-400">{tk.racket_lab_operator_id.slice(0, 10)}</code>
                                ) : null}
                                <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] text-slate-600">
                                  {t(labelKeyFor(TICKET_STATUS_LABEL_KEY, tk.status))}
                                </span>
                              </span>
                            </button>
                            <button
                              type="button"
                              data-testid={`rl-calendar-ticket-qr-${tk.id}`}
                              title={t("tickets.qr_canonical.print_view")}
                              onClick={() => navigate(`/admin/racket-lab/tickets/${tk.id}/qr-print`)}
                              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                            >
                              <QrCode className="h-3 w-3" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                  {selected.shifts.length ? (
                    <>
                      <p className="text-[10px] uppercase tracking-wide text-slate-500">{t("calendar.shifts_label")}</p>
                      <ul className="mt-1 space-y-1 text-xs">
                        {selected.shifts.map((sh) => (
                          <li key={sh.id} className="flex items-center justify-between gap-2">
                            <code className="text-[10px] text-slate-500">{sh.racket_lab_operator_id?.slice(0, 14)}</code>
                            <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[9px] text-sky-700">{sh.status}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                </>
              )}
            </section>

            {/* Programmable queue */}
            <section data-testid="rl-calendar-unscheduled" className="rounded-md border border-slate-200 bg-white p-4">
              <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                <CalendarDays className="h-3.5 w-3.5 text-slate-400" />
                {t("calendar.unscheduled_title")}
              </h2>
              {unscheduled.length === 0 ? (
                <p className="text-xs text-slate-500">{t("calendar.unscheduled_empty")}</p>
              ) : (
                <ul className="space-y-2 text-xs">
                  {unscheduled.map((tk) => (
                    <li key={tk.id} data-testid={`rl-calendar-unscheduled-${tk.id}`} className="flex flex-wrap items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/racket-lab/tickets/${tk.id}`)}
                        className="truncate text-left text-slate-700 hover:underline"
                      >
                        {tk.equipment_reference_label || tk.id.slice(0, 8)}
                      </button>
                      <span className="flex items-center gap-1.5">
                        <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] text-slate-600">
                          {t(labelKeyFor(TICKET_STATUS_LABEL_KEY, tk.status))}
                        </span>
                        <input
                          type="date"
                          data-testid={`rl-calendar-schedule-date-${tk.id}`}
                          value={scheduleDates[tk.id] || ""}
                          onChange={(e) => setScheduleDates((s) => ({ ...s, [tk.id]: e.target.value }))}
                          className="rounded-md border border-slate-300 px-1.5 py-0.5 text-[10px]"
                        />
                        <button
                          type="button"
                          data-testid={`rl-calendar-schedule-btn-${tk.id}`}
                          onClick={() => scheduleTicket(tk.id)}
                          disabled={!scheduleDates[tk.id] || mutating}
                          className="rounded-md bg-emerald-600 px-2 py-0.5 text-[10px] font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {t("calendar.schedule_btn")}
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-2 text-[9px] text-slate-400">{t("calendar.unscheduled_hint")}</p>
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default RLAdminCalendar;
